/**
 * VN-2000 <-> WGS84 coordinate conversion (pure JS, no dependencies).
 *
 * - Ellipsoid: WGS84 (VN-2000 also uses the WGS84 ellipsoid).
 * - Datum shift: 7-parameter Helmert, Position Vector convention
 *   (EPSG:6960 / Circular 973/2001/TT-TCĐC), applied in ECEF.
 * - Projection: Transverse Mercator, Krüger series to order n^6 (Karney 2011)
 *   -> sub-millimetre accuracy inside a 3/6 degree zone.
 *
 * VN-2000 convention: X = northing (m), Y = easting (m).
 */

const A = 6378137.0;
const F = 1 / 298.257223563;
const E2 = F * (2 - F);
const E = Math.sqrt(E2);
const N = F / (2 - F);
const N2 = N * N, N3 = N2 * N, N4 = N3 * N, N5 = N4 * N, N6 = N5 * N;
const RECT_A = (A / (1 + N)) * (1 + N2 / 4 + N4 / 64 + N6 / 256);

const ALPHA = [
  N / 2 - (2 / 3) * N2 + (5 / 16) * N3 + (41 / 180) * N4 - (127 / 288) * N5 + (7891 / 37800) * N6,
  (13 / 48) * N2 - (3 / 5) * N3 + (557 / 1440) * N4 + (281 / 630) * N5 - (1983433 / 1935360) * N6,
  (61 / 240) * N3 - (103 / 140) * N4 + (15061 / 26880) * N5 + (167603 / 181440) * N6,
  (49561 / 161280) * N4 - (179 / 168) * N5 + (6601661 / 7257600) * N6,
  (34729 / 80640) * N5 - (3418889 / 1995840) * N6,
  (212378941 / 319334400) * N6,
];
const BETA = [
  N / 2 - (2 / 3) * N2 + (37 / 96) * N3 - (1 / 360) * N4 - (81 / 512) * N5 + (96199 / 604800) * N6,
  (1 / 48) * N2 + (1 / 15) * N3 - (437 / 1440) * N4 + (46 / 105) * N5 - (1118711 / 3870720) * N6,
  (17 / 480) * N3 - (37 / 840) * N4 - (209 / 4480) * N5 + (5569 / 90720) * N6,
  (4397 / 161280) * N4 - (11 / 504) * N5 - (830251 / 7257600) * N6,
  (4583 / 161280) * N5 - (108847 / 3991680) * N6,
  (20648693 / 638668800) * N6,
];

const DEG = Math.PI / 180;
const SEC = DEG / 3600;

/** Helmert parameters VN-2000 -> WGS84 (Position Vector). */
export const HELMERT_VN2000_TO_WGS84 = Object.freeze({
  dx: -191.90441429, dy: -39.30318279, dz: -111.45032835,
  rx: -0.00928836, ry: 0.01975479, rz: -0.00427372, // arc-seconds
  ds: 0.252906278, // ppm
});

/** Known projection zones used for Hà Nội. */
export const ZONES = Object.freeze({
  'hn-105': { id: 'hn-105', label: 'VN-2000 Hà Nội · KTT 105°00′ · múi 3°', lon0: 105, k0: 0.9999, x0: 500000, y0: 0 },
  'utm-48': { id: 'utm-48', label: 'VN-2000 UTM · múi 48 (KTT 105°, múi 6°)', lon0: 105, k0: 0.9996, x0: 500000, y0: 0 },
});
export const DEFAULT_ZONE = 'hn-105';

const atanh = Math.atanh || ((x) => 0.5 * Math.log((1 + x) / (1 - x)));
const asinh = Math.asinh || ((x) => Math.log(x + Math.sqrt(x * x + 1)));

/* ---------------- Transverse Mercator (Krüger n^6) ---------------- */

/** Forward TM: geodetic (deg) -> { e: easting, n: northing } (m). */
export function tmForward(latDeg, lonDeg, zone) {
  const phi = latDeg * DEG;
  const lam = (lonDeg - zone.lon0) * DEG;
  const sphi = Math.sin(phi);
  // conformal latitude via tau' (Karney): t = sinh(atanh(sinφ) − e·atanh(e·sinφ))
  const t = Math.sinh(atanh(sphi) - E * atanh(E * sphi));
  const xiP = Math.atan2(t, Math.cos(lam));
  const etaP = atanh(Math.sin(lam) / Math.sqrt(1 + t * t));
  let xi = xiP, eta = etaP;
  for (let j = 1; j <= 6; j++) {
    const a = ALPHA[j - 1];
    xi += a * Math.sin(2 * j * xiP) * Math.cosh(2 * j * etaP);
    eta += a * Math.cos(2 * j * xiP) * Math.sinh(2 * j * etaP);
  }
  return { e: zone.x0 + zone.k0 * RECT_A * eta, n: zone.y0 + zone.k0 * RECT_A * xi };
}

/** Inverse TM: { e, n } (m) -> { lat, lon } (deg). */
export function tmInverse(e, n, zone) {
  const xi = (n - zone.y0) / (zone.k0 * RECT_A);
  const eta = (e - zone.x0) / (zone.k0 * RECT_A);
  let xiP = xi, etaP = eta;
  for (let j = 1; j <= 6; j++) {
    const b = BETA[j - 1];
    xiP -= b * Math.sin(2 * j * xi) * Math.cosh(2 * j * eta);
    etaP -= b * Math.cos(2 * j * xi) * Math.sinh(2 * j * eta);
  }
  const sinhEta = Math.sinh(etaP);
  const cosXi = Math.cos(xiP);
  const tauP = Math.sin(xiP) / Math.sqrt(sinhEta * sinhEta + cosXi * cosXi);
  const lam = Math.atan2(sinhEta, cosXi);
  // Newton iteration tau' -> tau (Karney 2011, eq. 19–21)
  let tau = tauP;
  for (let i = 0; i < 8; i++) {
    const s1 = Math.sqrt(1 + tau * tau);
    const sigma = Math.sinh(E * atanh((E * tau) / s1));
    const tauI = tau * Math.sqrt(1 + sigma * sigma) - sigma * s1;
    const dTau = ((tauP - tauI) / Math.sqrt(1 + tauI * tauI)) * ((1 + (1 - E2) * tau * tau) / ((1 - E2) * s1));
    tau += dTau;
    if (Math.abs(dTau) < 1e-14) break;
  }
  return { lat: Math.atan(tau) / DEG, lon: zone.lon0 + lam / DEG };
}

/** Point scale factor of the TM projection at a geodetic point (for info). */
export function tmScale(latDeg, lonDeg, zone) {
  // second-order approximation, adequate for display (≤1e-7 inside a zone)
  const phi = latDeg * DEG, l = (lonDeg - zone.lon0) * DEG;
  const c = Math.cos(phi);
  const ep2 = E2 / (1 - E2);
  const eta2 = ep2 * c * c;
  return zone.k0 * (1 + (l * l * c * c * (1 + eta2)) / 2 + (l ** 4 * c ** 4 * (5 - 4 * Math.tan(phi) ** 2)) / 24);
}

/* ---------------- Geodetic <-> ECEF + Helmert ---------------- */

function geodToEcef(latDeg, lonDeg, h = 0) {
  const phi = latDeg * DEG, lam = lonDeg * DEG;
  const sp = Math.sin(phi), cp = Math.cos(phi);
  const nu = A / Math.sqrt(1 - E2 * sp * sp);
  return [(nu + h) * cp * Math.cos(lam), (nu + h) * cp * Math.sin(lam), (nu * (1 - E2) + h) * sp];
}

function ecefToGeod(x, y, z) {
  const p = Math.hypot(x, y);
  const lon = Math.atan2(y, x);
  let lat = Math.atan2(z, p * (1 - E2));
  let h = 0;
  for (let i = 0; i < 10; i++) {
    const sp = Math.sin(lat);
    const nu = A / Math.sqrt(1 - E2 * sp * sp);
    h = p / Math.cos(lat) - nu;
    const next = Math.atan2(z, p * (1 - (E2 * nu) / (nu + h)));
    if (Math.abs(next - lat) < 1e-15) { lat = next; break; }
    lat = next;
  }
  return { lat: lat / DEG, lon: lon / DEG, h };
}

function helmertForward([x, y, z], p) {
  const rx = p.rx * SEC, ry = p.ry * SEC, rz = p.rz * SEC, s = 1 + p.ds * 1e-6;
  return [
    p.dx + s * (x - rz * y + ry * z),
    p.dy + s * (rz * x + y - rx * z),
    p.dz + s * (-ry * x + rx * y + z),
  ];
}

function helmertInverse([x, y, z], p) {
  // exact inverse of (I + S) is (I - S + S^2 - ...); rotations ~1e-7 rad → S^2 term < 1e-7 m
  const rx = p.rx * SEC, ry = p.ry * SEC, rz = p.rz * SEC, s = 1 + p.ds * 1e-6;
  const u = (x - p.dx) / s, v = (y - p.dy) / s, w = (z - p.dz) / s;
  // solve (I+S)·X = U with one refinement step for full precision
  let X = u + rz * v - ry * w;
  let Y = -rz * u + v + rx * w;
  let Z = ry * u - rx * v + w;
  const fx = X - rz * Y + ry * Z - u, fy = rz * X + Y - rx * Z - v, fz = -ry * X + rx * Y + Z - w;
  X -= fx; Y -= fy; Z -= fz;
  return [X, Y, Z];
}

/** WGS84 geodetic -> VN-2000 geodetic. */
export function wgs84ToVn2000Geod(lat, lon) {
  const g = ecefToGeod(...helmertInverse(geodToEcef(lat, lon, 0), HELMERT_VN2000_TO_WGS84));
  return { lat: g.lat, lon: g.lon };
}

/** VN-2000 geodetic -> WGS84 geodetic. */
export function vn2000GeodToWgs84(lat, lon) {
  const g = ecefToGeod(...helmertForward(geodToEcef(lat, lon, 0), HELMERT_VN2000_TO_WGS84));
  return { lat: g.lat, lon: g.lon };
}

function zoneOf(z) {
  if (!z) return ZONES[DEFAULT_ZONE];
  if (typeof z === 'string') return ZONES[z] || ZONES[DEFAULT_ZONE];
  return z;
}

/**
 * WGS84 (lat, lon) -> VN-2000 plane coordinates.
 * @returns {{x:number, y:number}} x = northing (X), y = easting (Y), metres.
 */
export function toVN2000(lat, lon, zone) {
  const zn = zoneOf(zone);
  const g = wgs84ToVn2000Geod(lat, lon);
  const p = tmForward(g.lat, g.lon, zn);
  return { x: p.n, y: p.e };
}

/** VN-2000 plane (x = northing, y = easting) -> WGS84 { lat, lon }. */
export function fromVN2000(x, y, zone) {
  const zn = zoneOf(zone);
  const g = tmInverse(y, x, zn);
  let w = vn2000GeodToWgs84(g.lat, g.lon);
  // The 2D datum shift is not exactly invertible (ellipsoidal height is dropped on each side).
  // Refine so that toVN2000(fromVN2000(x, y)) reproduces (x, y) to < 1e-6 m.
  for (let i = 0; i < 3; i++) {
    const p = toVN2000(w.lat, w.lon, zn);
    const dx = x - p.x, dy = y - p.y;
    if (Math.abs(dx) < 1e-7 && Math.abs(dy) < 1e-7) break;
    w = { lat: w.lat + dx / 110574.0, lon: w.lon + dy / (111319.49 * Math.cos(w.lat * DEG)) };
  }
  return w;
}

/**
 * Fast local planar projector (VN-2000 plane, origin-shifted for numeric stability).
 * Exact transform per vertex — use for area / overlay computations in metres.
 */
export function makePlanarProjector(zone) {
  const zn = zoneOf(zone);
  return {
    zone: zn,
    forward(lon, lat) { const p = toVN2000(lat, lon, zn); return [p.y, p.x]; }, // [E, N]
    inverse(e, n) { const g = fromVN2000(n, e, zn); return [g.lon, g.lat]; },
  };
}

/** Rough bounding ranges of Hà Nội in VN-2000 (with margin). */
export const HANOI_VN2000_RANGE = Object.freeze({ xMin: 2235000, xMax: 2400000, yMin: 470000, yMax: 640000 });

/**
 * Classify a pair of numbers as VN-2000 (X, Y). Accepts either order.
 * @returns {{x:number,y:number,swapped:boolean}|null}
 */
export function classifyVN2000Pair(a, b) {
  const R = HANOI_VN2000_RANGE;
  const isX = (v) => v >= R.xMin && v <= R.xMax;
  const isY = (v) => v >= R.yMin && v <= R.yMax;
  if (isX(a) && isY(b)) return { x: a, y: b, swapped: false };
  if (isY(a) && isX(b)) return { x: b, y: a, swapped: true };
  return null;
}

/** Format a VN-2000 coordinate (metres, 3 decimals with thousands grouping). */
export function formatVN(v, digits = 3) {
  const [i, d] = v.toFixed(digits).split('.');
  return i.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (d ? ',' + d : '');
}
